import React, { useState, useRef, useEffect } from 'react';
import type { SearchBarProps } from './types/search.types';
import { SearchInput } from './components/SearchInput';
import { SearchResultsDropdown } from './components/SearchResultsDropdown';
import { useSearchEngine } from './hooks/useSearchEngine';
import { useHighlight } from './hooks/useHighlight';

export const SearchBar: React.FC<SearchBarProps> = ({
  placeholder = "Buscar...",
  value = '',
  onChange,
  onSelect,
  datasets = [],
  className = '',
  showCommandPalette = true,
  onOpenCommandPalette,
  commandPaletteShortcut = "Ctrl+K",
  externalSections,
  isLoading = false,
}) => {
  const [showResults, setShowResults] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const {
    searchQuery,
    setSearchQuery,
    searchSections: internalSections,
    hasResults: internalHasResults,
    hasSearchText,
    shouldSearch: internalShouldSearch,
    clearSearch,
  } = useSearchEngine(externalSections ? [] : datasets);

  const searchSections = externalSections ?? internalSections;
  const hasResults = externalSections
    ? Object.values(externalSections).some(s => s.items.length > 0)
    : internalHasResults;
  const shouldSearch = externalSections
    ? searchQuery.trim().length >= 2
    : internalShouldSearch;

  const { renderHighlight } = useHighlight();

  // Sincronizar con props externas
  useEffect(() => {
    if (value !== undefined && value !== searchQuery) {
      setSearchQuery(value);
    }
  }, [value, searchQuery, setSearchQuery]);

  const handleSearchChange = (newValue: string) => {
    setSearchQuery(newValue);
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    const hasValidText = newValue.trim().length > 0;
    if (hasValidText) {
      timeoutRef.current = setTimeout(() => {
        setShowResults(true);
      }, externalSections ? 0 : 100);
    } else {
      setShowResults(false);
    }
    
    onChange?.(newValue);
  };

  const handleFocus = () => {
    if (searchQuery.length > 0) {
      setShowResults(true);
    }
  };

  const handleSelectResult = (type: string, item: unknown) => {
    onSelect?.(type, item);
    setShowResults(false);
  };

  const handleClear = () => {
    clearSearch();
    setShowResults(false);
    onChange?.('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.ctrlKey && e.key === 'k') {
      e.preventDefault();
      e.stopPropagation();
      onOpenCommandPalette?.();
      setShowResults(false);
      return;
    }
    
    if (e.key === 'Escape') {
      setShowResults(false);
    }
  };

  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k' && showCommandPalette) {
        e.preventDefault();
        e.stopPropagation();
        onOpenCommandPalette?.();
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown, { capture: true });
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown, { capture: true });
    };
  }, [showCommandPalette, onOpenCommandPalette]);

  useEffect(() => {
    const handleClickOutside = (event: PointerEvent) => {
      if (!searchContainerRef.current || !showResults) {
        return;
      }

      const targetNode = event.target;
      if (!(targetNode instanceof Node)) {
        return;
      }

      if (!searchContainerRef.current.contains(targetNode)) {
        setShowResults(false);
      }
    };

    document.addEventListener('pointerdown', handleClickOutside, true);
    return () => {
      document.removeEventListener('pointerdown', handleClickOutside, true);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [showResults]);

  return (
    <div ref={searchContainerRef} className={`relative ${className}`}>
      <SearchInput
        value={searchQuery}
        onChange={handleSearchChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        onClear={handleClear}
        placeholder={placeholder}
        showClearButton={hasSearchText}
        showCommandPalette={showCommandPalette}
        onOpenCommandPalette={onOpenCommandPalette}
        commandPaletteShortcut={commandPaletteShortcut}
      />
      
      <SearchResultsDropdown
        isOpen={showResults}
        sections={searchSections}
        searchQuery={searchQuery}
        hasResults={hasResults}
        shouldSearch={shouldSearch}
        isLoading={isLoading}
        onSelectResult={handleSelectResult}
        renderHighlight={renderHighlight}
      />
    </div>
  );
};